"use client";

import Link from "next/link";
import CallExpandBtn from "./CallExpandBtn";
import AnimateOnScroll from "./AnimateOnScroll";
import CustomButton from "./CustomButton";
import { useEffect, useRef } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
// import { useCounselorTeamMembers } from "@/hooks/useCounselorTeamMembers";
import { teamMembers } from "@/lib/teamMembers";

const CARD_GAP = 20;

type TeamMember = (typeof teamMembers)[number];

const TeamCard = ({ team }: { team: TeamMember }) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play();
          observer.disconnect();
        }
      },
      { threshold: 0.3 },
    );

    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  return (
    <Link href={`/teams/${team.slug.toLowerCase()}`} className="block h-full">
      <article
        className="relative h-[400px] w-full overflow-hidden rounded-[16px] sm:h-[450px] md:h-[500px] md:rounded-[20px] lg:h-[548px]"
        style={{
          border: "1px solid rgba(0, 109, 255, 0.20)",
          background: "#E2D5D5",
          boxShadow: "0 0 36.6px 0 rgba(255, 255, 255, 0.60) inset",
        }}
        aria-label={`team: ${team.name}`}
      >
        <video
          ref={videoRef}
          src={team.video}
          poster={team.image}
          muted
          playsInline
          preload="metadata"
          className="absolute inset-0 h-full w-full object-cover"
          aria-hidden
        />

        <div
          className="absolute"
          style={{
            width: 714.74,
            height: 287.151,
            bottom: -60,
            left: "50%",
            transform: "translateX(-50%) rotate(10.996deg)",
            background:
              team.theme === "red"
                ? "linear-gradient(0deg, #BB0205 0%, rgba(187, 2, 5, 0.00) 100%)"
                : team.theme === "yellow"
                  ? "linear-gradient(0deg, #A67C00 0%, rgba(166, 124, 0, 0.00) 100%)"
                  : "linear-gradient(0deg, #0251BB 0%, rgba(2, 81, 187, 0.00) 100%)",
            backdropFilter: "blur(12.435px)",
            WebkitMaskImage:
              "linear-gradient(to top, black 0%, black 45%, transparent 100%)",
            maskImage:
              "linear-gradient(to top, black 0%, black 45%, transparent 100%)",
          }}
        />

        <div className="absolute bottom-0 left-0 right-0 flex flex-col gap-1.5 p-4 sm:gap-2 sm:p-5">
          <div>
            <h3 className="font-crimsonText text-xl font-semibold leading-none text-white sm:text-2xl md:text-3xl">
              {team.name}
            </h3>
            <h3 className="font-crimsonText text-xl font-semibold leading-none text-white sm:text-2xl md:text-3xl">
              {team.title}
            </h3>
          </div>
          <p className="font-onest text-xs font-light leading-[110%] text-white sm:text-sm">
            {team.description.slice(0, 150)}...
          </p>
        </div>
      </article>
    </Link>
  );
};

const TeamsSlider = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  // const { teamMembers } = useCounselorTeamMembers();

  const scroll = (dir: 1 | -1) => {
    if (!scrollRef.current) return;
    const cardW = scrollRef.current.clientWidth / 3;
    scrollRef.current.scrollBy({
      left: dir * (cardW + CARD_GAP),
      behavior: "smooth",
    });
  };

  return (
    <section className="container bg-white">
      <div className="flex flex-col gap-8 px-4 sm:gap-10 sm:px-5 md:gap-12 lg:gap-[60px]">
        <div className="flex flex-col gap-6 md:gap-8 lg:flex-row lg:items-center lg:justify-between lg:gap-10">
          <AnimateOnScroll>
            <h2 className="heading text-[#02386D]">
              Find the <span className="text-[#2857A7]">Right Support</span>
              <br />
              for Where You Are
            </h2>
          </AnimateOnScroll>

          <div className="flex w-full shrink-0 flex-col gap-4 md:gap-5 lg:w-[526px]">
            <p className="font-onest text-[16px] font-normal leading-[110%] text-[#607090] sm:text-[18px] md:text-[20px]">
              Three connected paths of care designed to help you feel heard,
              supported, and ready to move forward.
            </p>
            <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:gap-4">
              <CallExpandBtn variant="dark" />
              <CustomButton
                label="Get in Touch"
                href="/contact"
                variant="primary"
                withIcon
                size="lg"
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col items-end gap-3 sm:gap-4">
          <div
            className="flex gap-3 sm:gap-4 md:gap-[27px]"
            role="group"
            aria-label="Team carousel navigation"
          >
            <button
              onClick={() => scroll(-1)}
              className="flex h-[50px] w-[50px] items-center justify-center rounded-[14px] border border-v3-secondary/20 bg-[#0000004D] text-white transition-colors duration-200 hover:bg-v3-secondary/80 sm:h-[60px] sm:w-[60px] sm:rounded-[16px] md:h-[70px] md:w-[70px] md:rounded-[20px]"
              aria-label="Previous team members"
            >
              <ArrowLeft
                size={20}
                strokeWidth={1.5}
                className="md:h-6 md:w-6"
              />
            </button>
            <button
              onClick={() => scroll(1)}
              className="flex h-[50px] w-[50px] items-center justify-center rounded-[14px] border border-v3-secondary/20 bg-[#0000004D] text-white transition-colors duration-200 hover:bg-v3-secondary/80 sm:h-[60px] sm:w-[60px] sm:rounded-[16px] md:h-[70px] md:w-[70px] md:rounded-[20px]"
              aria-label="Next team members"
            >
              <ArrowRight
                size={20}
                strokeWidth={1.5}
                className="md:h-6 md:w-6"
              />
            </button>
          </div>

          <div
            ref={scrollRef}
            className="flex w-full gap-3 overflow-x-auto scroll-smooth [scrollbar-width:none] sm:gap-4 md:gap-5 [&::-webkit-scrollbar]:hidden"
            role="list"
            aria-label="Team members"
          >
            {teamMembers.map((team) => (
              <div
                key={team.slug}
                role="listitem"
                className="w-[260px] shrink-0 sm:w-[280px] md:w-[320px] lg:w-[calc((100%-40px)/3)]"
              >
                <TeamCard team={team} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TeamsSlider;
